import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEscape, useLockBodyScroll } from "../lib/hooks";
import { useI18n } from "../i18n";

export default function Lightbox({ images, startIndex = 0, title, onClose }) {
  const { t } = useI18n();
  const [index, setIndex] = useState(startIndex);
  const touchX = useRef(null);
  const count = images.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + count) % count), [count]);

  useEscape(onClose);
  useLockBodyScroll(true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchX.current = null;
  };

  return createPortal(
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title || t("gallery.label")}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={onTouchEnd}
    >
      <button type="button" className="lightbox-close icon-btn" onClick={onClose} aria-label={t("gallery.close")}>
        <X size={22} />
      </button>
      <figure className="lightbox-figure">
        <img src={images[index]} alt={`${title || t("gallery.photo")}, ${t("gallery.of", { n: index + 1, total: count })}`} />
        {count > 1 && <figcaption>{index + 1} / {count}</figcaption>}
      </figure>
      {count > 1 && (
        <>
          <button type="button" className="lightbox-nav lightbox-nav--prev icon-btn" onClick={prev} aria-label={t("gallery.prev")}>
            <ChevronLeft size={26} />
          </button>
          <button type="button" className="lightbox-nav lightbox-nav--next icon-btn" onClick={next} aria-label={t("gallery.next")}>
            <ChevronRight size={26} />
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
